import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Box, Typography, Button, Table, TableBody, TableCell,
  TableHead, TableRow, Paper, IconButton, Dialog, DialogActions, DialogContent,
  DialogContentText, DialogTitle, Snackbar, Alert, TextField, Checkbox,
  Pagination, Stack, CircularProgress, Tooltip, Chip, Menu, MenuItem, ListItemIcon, ListItemText
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import PaymentsIcon from '@mui/icons-material/Payments';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import TouchAppIcon from '@mui/icons-material/TouchApp';
import InventoryIcon from '@mui/icons-material/Inventory';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';

import { getAllPdvs, deletePdv, getLicencaStatus } from '../services/api';
import useModulosLicenca from '../hooks/useModulosLicenca';
import {
  contarPdvsAtivos,
  podeCadastrarAlgumPdv,
  podeCadastrarPdvTipo,
} from '../utils/pdvLicenca';
import PageHeader from './common/PageHeader';
import ResponsiveTable from './common/ResponsiveTable';
import EntityFormDialog from './common/EntityFormDialog';
import PdvForm from './PdvForm';
import CopiarConfigPdvDialog from './CopiarConfigPdvDialog';
import PdvInstaladoresDialog from './PdvInstaladoresDialog';
import SortableTh from './common/SortableTh';
import { useClientSort } from '../hooks/useClientSort';
import { sxLinhaInativa } from './common/StatusAtivo';
import { getApiErrorMessage } from '../utils/apiError';

const TIPOS_NOVO_TERMINAL = [
  { tipo: 'caixa', Icon: PointOfSaleIcon, primary: 'Caixa (PDV)', secondary: 'Venda no balcão' },
  { tipo: 'zenpay', Icon: PaymentsIcon, primary: 'ZenPay (POS Stone)', secondary: 'Receptor TEF remoto' },
  { tipo: 'preVenda', Icon: ShoppingCartIcon, primary: 'Pré-venda', secondary: 'Monta pedido' },
  { tipo: 'autoAtendimento', Icon: TouchAppIcon, primary: 'Autoatendimento', secondary: 'Totem / quiosque' },
  { tipo: 'inventario', Icon: InventoryIcon, primary: 'Inventário', secondary: 'Contagem de estoque' },
];

export default function PdvList() {
  const { modulos } = useModulosLicenca();
  // paginação (no front, por enquanto)
  const [pagina, setPagina] = useState(1);     // 1-based
  const [itensPorPagina] = useState(20);

  // dados
  const [pdvs, setPdvs] = useState([]);
  const [recursosLicenca, setRecursosLicenca] = useState(null);
  const [carregando, setCarregando] = useState(false);

  // busca
  const [searchText, setSearchText] = useState('');
  const [termo, setTermo] = useState('');

  // seleção / diálogo / snackbar
  const [selecionados, setSelecionados] = useState([]);
  const [dialogoAberto, setDialogoAberto] = useState(false);
  const [idsParaExcluir, setIdsParaExcluir] = useState([]);
  const [snack, setSnack] = useState({ open: false, sev: 'success', msg: '' });
  const [form, setForm] = useState({ open: false, id: null, tipoTerminal: 'caixa' });
  const [novoMenuAnchor, setNovoMenuAnchor] = useState(null);
  const [copiar, setCopiar] = useState({ open: false, origemId: '' });
  const [instaladoresOpen, setInstaladoresOpen] = useState(false);

  const carregar = useCallback(async () => {
    try {
      setCarregando(true);
      const [pdvsRes, licencaRes] = await Promise.all([
        getAllPdvs(),
        getLicencaStatus().catch(() => ({ data: null })),
      ]);
      setPdvs(Array.isArray(pdvsRes.data) ? pdvsRes.data : []);
      setRecursosLicenca(licencaRes.data?.recursosLicenca ?? null);
      setSelecionados([]);
      setPagina(1); // reset ao carregar
    } catch {
      setSnack({ open: true, sev: 'error', msg: 'Erro ao carregar PDVs' });
    } finally {
      setCarregando(false);
    }
  }, []);

  const usoPdvLicenca = useMemo(() => contarPdvsAtivos(pdvs), [pdvs]);

  const ctxCadastroPdv = useMemo(
    () => ({
      uso: usoPdvLicenca,
      recursos: recursosLicenca,
      modulos,
    }),
    [usoPdvLicenca, recursosLicenca, modulos]
  );

  const opcoesNovoTerminal = useMemo(
    () => TIPOS_NOVO_TERMINAL.map((item) => ({
      ...item,
      cadastro: podeCadastrarPdvTipo(item.tipo, ctxCadastroPdv),
    })),
    [ctxCadastroPdv]
  );

  const podeCadastrarTerminal = useMemo(
    () => podeCadastrarAlgumPdv(ctxCadastroPdv),
    [ctxCadastroPdv]
  );

  const motivoBloqueioCadastro = useMemo(() => {
    if (podeCadastrarTerminal) return '';
    const caixa = podeCadastrarPdvTipo('caixa', ctxCadastroPdv);
    if (!caixa.ok) return caixa.motivo;
    return 'Nenhuma licença disponível para novos terminais.';
  }, [podeCadastrarTerminal, ctxCadastroPdv]);

  useEffect(() => { carregar(); }, [carregar]);

  // filtro local
  const filtrados = useMemo(() => {
    const t = termo.trim().toLowerCase();
    if (!t) return pdvs;
    return pdvs.filter(p =>
      (p.descricao || '').toLowerCase().includes(t) ||
      (p.codigoInstalacao || '').toLowerCase().includes(t) ||
      String(p.estabelecimentoId || '').includes(t)
    );
  }, [pdvs, termo]);

  // paginação local
  const totalItens = filtrados.length;
  const totalPaginas = Math.max(1, Math.ceil(totalItens / itensPorPagina));
  const inicio = (pagina - 1) * itensPorPagina;
  const pdvAccessors = useMemo(() => ({
    ativo: (p) => (p.ativo ? 1 : 0),
    modo: (p) => (
      p.zenPay ? 'ZenPay'
        : p.gerencial ? 'Inventário'
          : p.autoAtendimento ? 'Autoatendimento'
            : p.preVenda ? 'Pré-venda'
              : 'PDV'
    ),
    tef: (p) => (p.zenPay ? 'Receptor' : p.tefAtivo ? (p.tefTipo || 'SiTEF') : ''),
  }), []);
  const { sorted: ordenados, orderBy, order, requestSort } = useClientSort(
    filtrados,
    'descricao',
    'asc',
    pdvAccessors,
  );
  const paginaAtual = ordenados.slice(inicio, inicio + itensPorPagina);
  const paginaOrdenada = paginaAtual;

  // busca
  const onBuscar = () => setTermo(searchText);
  const onKeyDown = (e) => e.key === 'Enter' && onBuscar();

  // seleção
  const selecionarTodos = (e) => {
    setSelecionados(e.target.checked ? paginaAtual.map(p => p.id) : []);
  };
  const selecionar = (id) => {
    setSelecionados(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };
  const todosSelecionados = useMemo(
    () => paginaAtual.length > 0 && selecionados.length === paginaAtual.length,
    [paginaAtual, selecionados]
  );
  const indeterminate = selecionados.length > 0 && !todosSelecionados;

  // exclusão
  const abrirDialogo = (ids) => {
    setIdsParaExcluir(Array.isArray(ids) ? ids : [ids]);
    setDialogoAberto(true);
  };
  const fecharDialogo = () => { setDialogoAberto(false); setIdsParaExcluir([]); };
  const excluir = async () => {
    if (idsParaExcluir.length === 0) return;
    try {
      await Promise.all(idsParaExcluir.map(id => deletePdv(id)));
      setSnack({
        open: true,
        sev: 'success',
        msg: idsParaExcluir.length > 1
          ? `${idsParaExcluir.length} PDVs excluídos.`
          : 'PDV excluído.'
      });
      await carregar();
      // ajustar página se esvaziou
      const sobrou = filtrados.length - idsParaExcluir.length;
      if (sobrou <= (pagina - 1) * itensPorPagina && pagina > 1) {
        setPagina(pagina - 1);
      }
    } catch (e) {
      setSnack({
        open: true,
        sev: 'error',
        msg: getApiErrorMessage(e, 'Não foi possível excluir o PDV.'),
      });
    } finally {
      fecharDialogo();
    }
  };

  const abrirForm = (id = null, tipoTerminal = 'caixa') => setForm({ open: true, id, tipoTerminal });
  const fecharForm = () => setForm((f) => ({ ...f, open: false }));
  const aoSalvar = (msg) => {
    fecharForm();
    setSnack({ open: true, sev: 'success', msg });
    carregar();
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <PageHeader
        title="PDVs"
        subtitle="Terminais autorizados a sincronizar vendas com o ZenGestão."
        actions={
          <>
            {selecionados.length > 0 && (
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => abrirDialogo(selecionados)}
              >
                Excluir selecionados ({selecionados.length})
              </Button>
            )}
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={() => setInstaladoresOpen(true)}
            >
              Baixar instaladores
            </Button>
            <Button
              variant="outlined"
              startIcon={<ContentCopyIcon />}
              onClick={() => setCopiar({ open: true, origemId: '' })}
              disabled={pdvs.length < 2}
            >
              Copiar configuração
            </Button>
            <Tooltip title={podeCadastrarTerminal ? '' : motivoBloqueioCadastro}>
              <span>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  endIcon={<ArrowDropDownIcon />}
                  disabled={!podeCadastrarTerminal}
                  onClick={(e) => setNovoMenuAnchor(e.currentTarget)}
                >
                  Novo terminal
                </Button>
              </span>
            </Tooltip>
            <Menu
              anchorEl={novoMenuAnchor}
              open={Boolean(novoMenuAnchor)}
              onClose={() => setNovoMenuAnchor(null)}
            >
              {opcoesNovoTerminal.map(({ tipo, Icon, primary, secondary, cadastro }) => (
                <Tooltip
                  key={tipo}
                  title={cadastro.ok ? '' : cadastro.motivo}
                  placement="left"
                  disableHoverListener={cadastro.ok}
                >
                  <span>
                    <MenuItem
                      disabled={!cadastro.ok}
                      onClick={() => {
                        if (!cadastro.ok) return;
                        setNovoMenuAnchor(null);
                        abrirForm(null, tipo);
                      }}
                    >
                      <ListItemIcon><Icon fontSize="small" /></ListItemIcon>
                      <ListItemText primary={primary} secondary={secondary} />
                    </MenuItem>
                  </span>
                </Tooltip>
              ))}
            </Menu>
          </>
        }
      />

      {/* Busca */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <TextField
          size="small"
          placeholder="Buscar por descrição, código de instalação ou estabelecimento..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          onKeyDown={onKeyDown}
          InputProps={{
            startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
            endAdornment: !!searchText && (
              <IconButton size="small" onClick={() => setSearchText('')}>
                <CloseIcon fontSize="small" />
              </IconButton>
            )
          }}
          sx={{ minWidth: 320, flex: 2 }}
        />
        <Button variant="outlined" onClick={onBuscar}>Buscar</Button>
      </Paper>

      {/* Tabela */}
      <ResponsiveTable>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={todosSelecionados}
                  indeterminate={indeterminate}
                  onChange={selecionarTodos}
                />
              </TableCell>
              <SortableTh id="descricao" label="Descrição" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="codigoInstalacao" label="Código Instalação" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="estabelecimentoId" label="Estabelecimento" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="ativo" label="Ativo" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="plataforma" label="Plataforma" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="modo" label="Modo" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="tef" label="TEF" orderBy={orderBy} order={order} onSort={requestSort} />
              <TableCell align="right"><strong>Ações</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {carregando ? (
              <TableRow>
                <TableCell colSpan={9} align="center"><CircularProgress size={24} /></TableCell>
              </TableRow>
            ) : paginaAtual.length > 0 ? (
              paginaOrdenada.map(p => (
                <TableRow key={p.id} hover sx={{ cursor: 'pointer', ...sxLinhaInativa(p.ativo) }} onClick={() => abrirForm(p.id)}>
                  <TableCell padding="checkbox" data-label="Selecionar" onClick={(e) => e.stopPropagation()}>
                    <Checkbox checked={selecionados.includes(p.id)} onChange={() => selecionar(p.id)} />
                  </TableCell>
                  <TableCell data-label="Descrição">{p.descricao || '-'}</TableCell>
                  <TableCell data-label="Código instalação">{p.codigoInstalacao || '-'}</TableCell>
                  <TableCell data-label="Estabelecimento">{p.estabelecimentoId ?? '-'}</TableCell>
                  <TableCell data-label="Ativo">
                    <Tooltip title={!p.ativo && p.mensagemBloqueio ? p.mensagemBloqueio : ''}>
                      <Chip
                        size="small"
                        label={p.ativo ? 'Ativo' : 'Inativo'}
                        color={p.ativo ? 'success' : 'default'}
                      />
                    </Tooltip>
                  </TableCell>
                  <TableCell data-label="Plataforma">
                    {p.plataforma || '—'}
                    {p.versaoApp ? (
                      <Typography component="span" variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {p.versaoApp}
                      </Typography>
                    ) : null}
                  </TableCell>
                  <TableCell data-label="Modo">
                    {p.zenPay
                      ? 'ZenPay'
                      : p.gerencial
                        ? 'Inventário'
                        : p.autoAtendimento
                          ? 'Autoatendimento'
                          : p.preVenda
                            ? 'Pré-venda'
                            : 'PDV'}
                  </TableCell>
                  <TableCell data-label="TEF">
                    {p.zenPay ? (
                      <Chip size="small" color="secondary" label="Receptor" />
                    ) : p.tefAtivo ? (
                      <Chip
                        size="small"
                        color="primary"
                        label={(p.tefTipo || 'SiTEF') === 'ZenPay' ? 'ZenPay' : 'SiTef'}
                      />
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  <TableCell align="right" data-actions="" onClick={(e) => e.stopPropagation()}>
                    <Tooltip title="Copiar configuração deste PDV">
                      <IconButton
                        color="secondary"
                        onClick={() => setCopiar({ open: true, origemId: p.id })}
                        disabled={pdvs.length < 2}
                      >
                        <ContentCopyIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Editar">
                      <IconButton color="primary" onClick={() => abrirForm(p.id)}><EditIcon /></IconButton>
                    </Tooltip>
                    <Tooltip title="Excluir">
                      <IconButton color="error" onClick={() => abrirDialogo(p.id)}><DeleteIcon /></IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  Nenhum PDV encontrado
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </ResponsiveTable>

      {/* Paginação (front) */}
      <Stack spacing={2} sx={{ mt: 2, alignItems: 'center' }}>
        <Pagination count={totalPaginas} page={pagina} onChange={(_e, p) => setPagina(p)} color="primary" />
        <Typography variant="body2" color="text.secondary">
          Exibindo {paginaAtual.length} de {totalItens} PDVs (página {pagina}/{totalPaginas})
        </Typography>
      </Stack>

      <EntityFormDialog
        open={form.open}
        title={
          form.id
            ? 'Editar PDV'
            : ({
              caixa: 'Novo caixa (PDV)',
              zenpay: 'Novo ZenPay (POS)',
              preVenda: 'Nova pré-venda',
              autoAtendimento: 'Novo autoatendimento',
              inventario: 'Novo terminal de inventário',
            }[form.tipoTerminal] || 'Novo terminal')
        }
        subtitle={form.id ? `Código ${form.id}` : null}
        onClose={fecharForm}
        maxWidth="lg"
        fullHeight
      >
        <PdvForm
          key={`${form.id || 'novo'}-${form.tipoTerminal}`}
          embedded
          pdvId={form.id}
          initialTipoTerminal={form.tipoTerminal}
          onCancel={fecharForm}
          onSaved={aoSalvar}
        />
      </EntityFormDialog>

      <CopiarConfigPdvDialog
        open={copiar.open}
        origemIdInicial={copiar.origemId}
        pdvs={pdvs}
        onClose={() => setCopiar({ open: false, origemId: '' })}
        onSuccess={(msg) => {
          setSnack({ open: true, sev: 'success', msg });
          carregar();
        }}
      />

      <PdvInstaladoresDialog
        open={instaladoresOpen}
        onClose={() => setInstaladoresOpen(false)}
      />

      {/* Diálogo */}
      <Dialog open={dialogoAberto} onClose={fecharDialogo}>
        <DialogTitle>{idsParaExcluir.length > 1 ? 'Excluir PDVs' : 'Excluir PDV'}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {idsParaExcluir.length > 1
              ? `Tem certeza que deseja excluir os ${idsParaExcluir.length} PDVs selecionados?`
              : 'Tem certeza que deseja excluir este PDV?'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={fecharDialogo} color="inherit">Cancelar</Button>
          <Button onClick={excluir} color="error" autoFocus>Excluir</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snack.open}
        autoHideDuration={5000}
        onClose={() => setSnack(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snack.sev} onClose={() => setSnack(s => ({ ...s, open: false }))}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
